// src/components/Sidebar.js
//
// Left sidebar navigation
// =======================
//
// PURPOSE
// -------
// This component renders the main internal navigation menu for the authenticated
// app shell.
//
// THIS VERSION
// ------------
// This version starts from the existing Sidebar.js and keeps the current visual
// character of the application:
// - same sidebar background color direction,
// - same font inheritance,
// - same menu structure,
// - same route auto-open logic,
// - same mobile close behavior.
//
// It adds only the next UI improvements:
// - HB Visits brand header,
// - first-level icons similar to the approved mockup,
// - desktop collapse / expand behavior,
// - body class synchronization so the main content can stay attached to the
//   right edge of the sidebar in both normal and collapsed mode.
//
// IMPORTANT LAYOUT NOTE
// ---------------------
// Collapse works by toggling:
//
//   body.sidebar-collapsed
//
// Sidebar.css then changes:
// - .sidebar width
// - .main-content margin-left / width
//
// If your DashboardLayout uses a different class than .main-content for the
// main app area, add the same CSS rule for that class or send me the layout file.
//
// RESPONSIBILITIES
// ----------------
// - render stable internal navigation links,
// - auto-open relevant dropdown groups based on current route,
// - support second-level navigation groups inside Reports,
// - support third-level grouping inside "Visits Analytics",
// - close the sidebar on mobile after navigation,
// - support desktop sidebar collapse.
//
// SECURITY NOTE
// -------------
// Sidebar is not a security mechanism.
// Backend authorization remains authoritative.

import React, { useContext, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./Sidebar.css";
import AuthContext from "../context/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";

/* -------------------------------------------------------------------------- */
/* Collapse constants                                                         */
/* -------------------------------------------------------------------------- */

const SIDEBAR_COLLAPSED_STORAGE_KEY = "hb_visits_sidebar_collapsed";

/* -------------------------------------------------------------------------- */
/* Icon helpers                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Lightweight inline SVG icon.
 *
 * Why inline SVG?
 * - no new dependency,
 * - predictable look,
 * - easy to style with currentColor,
 * - close to the thin white line icons from the mockup.
 */
function SidebarIcon({ name }) {
  return (
    <span className="sidebar-icon" aria-hidden="true">
      {name === "dashboard" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M4 11.5 12 5l8 6.5" />
          <path d="M6.5 10.5V19h4.2v-4.8h2.6V19h4.2v-8.5" />
        </svg>
      )}

      {name === "masterData" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M5.5 6.5c0-1.7 2.9-3 6.5-3s6.5 1.3 6.5 3-2.9 3-6.5 3-6.5-1.3-6.5-3Z" />
          <path d="M5.5 6.5v5c0 1.7 2.9 3 6.5 3s6.5-1.3 6.5-3v-5" />
          <path d="M5.5 11.5v5c0 1.7 2.9 3 6.5 3s6.5-1.3 6.5-3v-5" />
        </svg>
      )}

      {name === "upload" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M7 18.5h10a4 4 0 0 0 .5-7.97A5.7 5.7 0 0 0 6.4 9.1 4.7 4.7 0 0 0 7 18.5Z" />
          <path d="M12 15.5V8.5" />
          <path d="M9.5 11 12 8.5l2.5 2.5" />
        </svg>
      )}

      {name === "visits" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M7 4.5h10" />
          <path d="M7 19.5h10" />
          <path d="M8 4.5c0 3.4 1.2 5.4 4 7.5-2.8 2.1-4 4.1-4 7.5" />
          <path d="M16 4.5c0 3.4-1.2 5.4-4 7.5 2.8 2.1 4 4.1 4 7.5" />
        </svg>
      )}

      {name === "hr" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M9 11.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
          <path d="M15.5 10.8a2.4 2.4 0 1 0 0-4.8" />
          <path d="M4.5 19c.5-3.1 2.1-5 4.5-5s4 1.9 4.5 5" />
          <path d="M13.8 14.2c2.5.3 4.1 1.9 4.7 4.8" />
        </svg>
      )}

      {name === "vouchers" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M7.5 7.5c2.2.1 3.4 1.3 4.5 3.3 1.1-2 2.3-3.2 4.5-3.3 1.8-.1 3 1.2 3 3 0 2.9-3.2 5.2-7.5 8-4.3-2.8-7.5-5.1-7.5-8 0-1.8 1.2-3.1 3-3Z" />
          <path d="M12 10.8v7.7" />
        </svg>
      )}

      {name === "catalog" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M7 4.5v3" />
          <path d="M17 4.5v3" />
          <path d="M5.5 7h13A1.5 1.5 0 0 1 20 8.5v10A1.5 1.5 0 0 1 18.5 20h-13A1.5 1.5 0 0 1 4 18.5v-10A1.5 1.5 0 0 1 5.5 7Z" />
          <path d="M4 11h16" />
          <path d="M8 14h2" />
          <path d="M14 14h2" />
          <path d="M8 17h2" />
        </svg>
      )}

      {name === "sales" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M6 3h12v18l-2-1.2-2 1.2-2-1.2-2 1.2-2-1.2L6 21V3Z" />
          <path d="M9 7h6" />
          <path d="M9 11h6" />
          <path d="M9 15h4" />
        </svg>
      )}

      {name === "webShop" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M6.5 7.5h11l-1.2 7.2a2 2 0 0 1-2 1.7H9.1a2 2 0 0 1-2-1.7L5.8 5.5H3.8" />
          <path d="M8.8 19.3h.1" />
          <path d="M15.4 19.3h.1" />
          <path d="M8 10.5h8" />
        </svg>
      )}

      {name === "stock" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M5 7.5 12 4l7 3.5-7 3.5-7-3.5Z" />
          <path d="M5 7.5v8.8l7 3.7 7-3.7V7.5" />
          <path d="M12 11v9" />
          <path d="M8.5 9.2v3.4" />
          <path d="M15.5 9.2v3.4" />
        </svg>
      )}


      {name === "sanitary" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M8.5 4.5h7" />
          <path d="M10 4.5v4.2l-4.8 8.1A2 2 0 0 0 6.9 20h10.2a2 2 0 0 0 1.7-3.2L14 8.7V4.5" />
          <path d="M8.2 15.5h7.6" />
          <path d="M10 12.8h4" />
          <path d="M9.2 17.8h5.6" />
        </svg>
      )}

      {name === "reports" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M6 6h1.5l1.2 9.2A2 2 0 0 0 10.7 17h5.8a2 2 0 0 0 1.9-1.4L20 10H8.2" />
          <path d="M10 20h.1" />
          <path d="M17 20h.1" />
        </svg>
      )}

      {name === "admin" && (
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z" />
          <path d="M19 13.2v-2.4l-2-.4a6.4 6.4 0 0 0-.8-1.8l1.1-1.7-1.7-1.7-1.7 1.1a6.4 6.4 0 0 0-1.8-.8L11.6 3H9.2l-.4 2a6.4 6.4 0 0 0-1.8.8L5.3 4.7 3.6 6.4l1.1 1.7a6.4 6.4 0 0 0-.8 1.8l-2 .4v2.4l2 .4a6.4 6.4 0 0 0 .8 1.8l-1.1 1.7 1.7 1.7L7 17.2a6.4 6.4 0 0 0 1.8.8l.4 2h2.4l.4-2a6.4 6.4 0 0 0 1.8-.8l1.7 1.1 1.7-1.7-1.1-1.7a6.4 6.4 0 0 0 .8-1.8l2-.4Z" />
        </svg>
      )}
    </span>
  );
}

/**
 * Top-level link content.
 */
function TopLinkContent({ icon, children }) {
  return (
    <>
      <SidebarIcon name={icon} />
      <span className="sidebar-label">{children}</span>
    </>
  );
}

/**
 * Top-level dropdown button content.
 */
function TopDropdownContent({ icon, label }) {
  return (
    <>
      <span className="sidebar-dropdown-main">
        <SidebarIcon name={icon} />
        <span className="sidebar-label">{label}</span>
      </span>
      <span className="caret">▼</span>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function routeMatchesAny(pathname, candidates = []) {
  const path = String(pathname || "");

  return candidates.some((candidate) => {
    const c = String(candidate || "");
    return path === c || path.startsWith(`${c}/`);
  });
}

function closeSidebarOnMobile() {
  if (window.innerWidth <= 991) {
    document.body.classList.remove("sidebar-open");
  }
}

function buildNavLinkClassName({ isActive }) {
  return isActive ? "active sidebar-link" : "sidebar-link";
}

function readInitialCollapsedState() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Reports route groups                                                       */
/* -------------------------------------------------------------------------- */

const FINANCIAL_REPORT_ROUTES = [
  "/reports/income",
  "/reports/commission",
];

const HR_REPORT_ROUTES = [
  "/reports/hr/absence-payroll",
];

const VISITS_ANALYTICS_CAPACITY_ROUTES = [
  "/reports/slot-performance",
  "/reports/idle-windows",
  "/reports/daily-schedule-quality",
];

const VISITS_ANALYTICS_ECONOMICS_ROUTES = [
  "/reports/service-profitability",
  "/reports/category-economics",
  "/reports/weekday-economics",
  "/reports/premium-window",
  "/reports/premium-windows",
];

const VISITS_ANALYTICS_CLIENT_ROUTES = [
  "/reports/no-show-loss",
  "/reports/client-retention-lite",
];

const VISITS_ANALYTICS_TREND_ROUTES = [
  "/reports/trend-seasonality",
];

const VISITS_ANALYTICS_REPORT_ROUTES = [
  ...VISITS_ANALYTICS_CAPACITY_ROUTES,
  ...VISITS_ANALYTICS_ECONOMICS_ROUTES,
  ...VISITS_ANALYTICS_CLIENT_ROUTES,
  ...VISITS_ANALYTICS_TREND_ROUTES,
];

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const Sidebar = () => {
  const { authState } = useContext(AuthContext);
  const location = useLocation();
  const { t } = useTranslation(["navigation", "common"]);

  const role = normalizeRole(authState?.user?.role);

  const [isCollapsed, setIsCollapsed] = useState(readInitialCollapsedState);

  const capabilities = useMemo(() => {
    const isSuperuser = role === "superuser";
    const isAdmin = role === "admin";
    const isReception = role === "reception";

    return {
      isSuperuser,
      isAdmin,
      isReception,
      canSeeReports: !isReception,
      canSeeIncomeReport: isSuperuser,
      canSeeHrReports: isSuperuser,
      canSeeAdministration: isSuperuser,
      canSeeApprovals: Boolean(authState?.isAuthenticated),
      canManageWorkSchedules: isSuperuser || isReception,
      // Master Data is intentionally a separate top-level module, not part
      // of Admin/User Management. Sidebar visibility stays broad for now;
      // backend authorization will remain authoritative when persistence is
      // introduced.
      canSeeMasterData: Boolean(authState?.isAuthenticated),
      canSeeCatalog: Boolean(authState?.isAuthenticated),
      canManageStockSettings: isSuperuser,
      // Reception sales is an operational internal module. Backend routes still
      // enforce roles; this flag only controls sidebar UX visibility.
      canSeeSales: Boolean(authState?.isAuthenticated),
      // Web Shop orders are readable for all authenticated internal users.
      // Resend/support actions remain superuser-only on the backend.
      canSeeWebShop: Boolean(authState?.isAuthenticated),
      // Phase 1 stock visibility follows the same broad internal-app rule as
      // catalog visibility. Backend routes still enforce read/write roles.
      canSeeStock: Boolean(authState?.isAuthenticated),
      // Sanitary documentation starts as an internal operational module.
      // Backend routes remain authoritative for edit permissions.
      canSeeSanitary: Boolean(authState?.isAuthenticated),
    };
  }, [role, authState?.isAuthenticated]);

  const [isVisitsOpen, setIsVisitsOpen] = useState(false);
  const [isHrOpen, setIsHrOpen] = useState(false);
  const [isVouchersOpen, setIsVouchersOpen] = useState(false);
  const [isMasterDataOpen, setIsMasterDataOpen] = useState(false);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [isSalesOpen, setIsSalesOpen] = useState(false);
  const [isWebShopOpen, setIsWebShopOpen] = useState(false);
  const [isStockOpen, setIsStockOpen] = useState(false);
  const [isSanitaryOpen, setIsSanitaryOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);

  const [isFinancialReportsOpen, setIsFinancialReportsOpen] = useState(false);
  const [isHrReportsOpen, setIsHrReportsOpen] = useState(false);
  const [isVisitsAnalyticsOpen, setIsVisitsAnalyticsOpen] = useState(false);

  const [isVisitsCapacityOpen, setIsVisitsCapacityOpen] = useState(false);
  const [isVisitsEconomicsOpen, setIsVisitsEconomicsOpen] = useState(false);
  const [isVisitsClientOpen, setIsVisitsClientOpen] = useState(false);
  const [isVisitsTrendOpen, setIsVisitsTrendOpen] = useState(false);

  /**
   * Synchronize collapsed state with body class.
   *
   * Sidebar.css uses this body class to move .main-content so the page content
   * always touches the right edge of the sidebar.
   */
  useEffect(() => {
    document.body.classList.toggle("sidebar-collapsed", isCollapsed);

    try {
      localStorage.setItem(
        SIDEBAR_COLLAPSED_STORAGE_KEY,
        isCollapsed ? "true" : "false"
      );
    } catch {
      // localStorage can be blocked in some browser modes.
    }

    return () => {
      document.body.classList.remove("sidebar-collapsed");
    };
  }, [isCollapsed]);

  useEffect(() => {
    const path = location.pathname;

    setIsVisitsOpen(routeMatchesAny(path, ["/manage", "/visits"]));
    setIsHrOpen(routeMatchesAny(path, ["/hr"]));
    setIsVouchersOpen(routeMatchesAny(path, ["/vouchers"]));
    setIsMasterDataOpen(routeMatchesAny(path, ["/master-data"]));
    setIsCatalogOpen(routeMatchesAny(path, ["/catalog"]));
    setIsSalesOpen(routeMatchesAny(path, ["/sales"]));
    setIsWebShopOpen(routeMatchesAny(path, ["/web-shop"]));
    setIsStockOpen(routeMatchesAny(path, ["/stock"]));
    setIsSanitaryOpen(routeMatchesAny(path, ["/sanitary"]));
    setIsReportsOpen(routeMatchesAny(path, ["/reports"]));
    setIsAdminOpen(routeMatchesAny(path, ["/admin-panel", "/admin", "/users"]));

    setIsFinancialReportsOpen(routeMatchesAny(path, FINANCIAL_REPORT_ROUTES));
    setIsHrReportsOpen(routeMatchesAny(path, HR_REPORT_ROUTES));
    setIsVisitsAnalyticsOpen(routeMatchesAny(path, VISITS_ANALYTICS_REPORT_ROUTES));

    setIsVisitsCapacityOpen(routeMatchesAny(path, VISITS_ANALYTICS_CAPACITY_ROUTES));
    setIsVisitsEconomicsOpen(routeMatchesAny(path, VISITS_ANALYTICS_ECONOMICS_ROUTES));
    setIsVisitsClientOpen(routeMatchesAny(path, VISITS_ANALYTICS_CLIENT_ROUTES));
    setIsVisitsTrendOpen(routeMatchesAny(path, VISITS_ANALYTICS_TREND_ROUTES));
  }, [location.pathname]);

  function toggleCollapsed() {
    setIsCollapsed((prev) => !prev);
  }

  /**
   * In collapsed mode we do not render submenus.
   * If user clicks a dropdown while collapsed, expand first and open the group.
   */
  function toggleDropdown(setter) {
    if (isCollapsed) {
      setIsCollapsed(false);
      setter(true);
      return;
    }

    setter((prev) => !prev);
  }

  return (
    <div
      className={`sidebar ${isCollapsed ? "sidebar--collapsed" : ""}`}
      role="navigation"
      aria-label={t("navigation:sidebarNavigationAriaLabel", {
        defaultValue: "Sidebar navigation",
      })}
    >
      <div className="d-lg-none text-end p-2">
        <button
          type="button"
          className="btn btn-sm btn-light"
          aria-label={t("navigation:closeMenu")}
          onClick={() => document.body.classList.remove("sidebar-open")}
        >
          ✕
        </button>
      </div>

      <div className="sidebar-brand">
        <NavLink
          to="/dashboard"
          onClick={closeSidebarOnMobile}
          className="sidebar-brand-link"
          title="HB Visits"
        >
          <span className="sidebar-brand-logo">HB</span>
          <span className="sidebar-brand-text">HB Visits</span>
        </NavLink>
      </div>

      <div className="sidebar-scroll">
        <ul className="sidebar-menu">
          <li>
            <NavLink
              to="/dashboard"
              onClick={closeSidebarOnMobile}
              className={buildNavLinkClassName}
              title={isCollapsed ? t("navigation:dashboard") : undefined}
            >
              <TopLinkContent icon="dashboard">{t("navigation:dashboard")}</TopLinkContent>
            </NavLink>
          </li>

          {/*
            Master Data is placed directly below Dashboard because it becomes a
            foundational configuration area for the whole application, not only
            an admin/user-management dictionary area.

            Current scope:
            - Company Master Data page is implemented as the first structured
              frontend foundation for future invoice printing.
            - Remaining entries are routed to lightweight placeholder pages so
              the menu structure is visible without creating broken navigation.
          */}
          {capabilities.canSeeMasterData && (
            <li className="sidebar-dropdown">
              <button
                type="button"
                className={`sidebar-dropdown-btn ${isMasterDataOpen ? "open" : ""}`}
                onClick={() => toggleDropdown(setIsMasterDataOpen)}
                aria-expanded={isMasterDataOpen && !isCollapsed}
                aria-controls="submenu-master-data"
                title={isCollapsed ? t("navigation:masterData") : undefined}
              >
                <TopDropdownContent icon="masterData" label={t("navigation:masterData")} />
              </button>

              {!isCollapsed && isMasterDataOpen && (
                <ul id="submenu-master-data" className="sidebar-submenu">
                  <li>
                    <NavLink
                      to="/master-data/company"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:companyMasterData")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/master-data/document-settings"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:documentSettings")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/master-data/payment-methods"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:paymentMethods")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/master-data/tax-settings"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:taxSettings")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/master-data/sales-number-series"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:salesNumberSeries")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/master-data/locations-branches"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:locationsBranches")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/master-data/legal-texts"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:legalTexts")}
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}

          <li>
            <NavLink
              to="/upload"
              onClick={closeSidebarOnMobile}
              className={buildNavLinkClassName}
              title={isCollapsed ? t("navigation:uploadData") : undefined}
            >
              <TopLinkContent icon="upload">{t("navigation:uploadData")}</TopLinkContent>
            </NavLink>
          </li>

          <li className="sidebar-dropdown">
            <button
              type="button"
              className={`sidebar-dropdown-btn ${isVisitsOpen ? "open" : ""}`}
              onClick={() => toggleDropdown(setIsVisitsOpen)}
              aria-expanded={isVisitsOpen && !isCollapsed}
              aria-controls="submenu-visits"
              title={isCollapsed ? t("navigation:visits") : undefined}
            >
              <TopDropdownContent icon="visits" label={t("navigation:visits")} />
            </button>

            {!isCollapsed && isVisitsOpen && (
              <ul id="submenu-visits" className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/visits/table"
                    onClick={closeSidebarOnMobile}
                    className={({ isActive }) =>
                      isActive || location.pathname === "/manage"
                        ? "active sidebar-link"
                        : "sidebar-link"
                    }
                  >
                    {t("navigation:visitsTable")}
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/visits/calendar"
                    onClick={closeSidebarOnMobile}
                    className={buildNavLinkClassName}
                  >
                    {t("navigation:visitsCalendar")}
                  </NavLink>
                </li>
              </ul>
            )}
          </li>

          <li className="sidebar-dropdown">
            <button
              type="button"
              className={`sidebar-dropdown-btn ${isHrOpen ? "open" : ""}`}
              onClick={() => toggleDropdown(setIsHrOpen)}
              aria-expanded={isHrOpen && !isCollapsed}
              aria-controls="submenu-hr"
              title={isCollapsed ? t("navigation:hr") : undefined}
            >
              <TopDropdownContent icon="hr" label={t("navigation:hr")} />
            </button>

            {!isCollapsed && isHrOpen && (
              <ul id="submenu-hr" className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/hr/my-settings"
                    onClick={closeSidebarOnMobile}
                    className={buildNavLinkClassName}
                  >
                    {t("navigation:myHrSettings")}
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/hr/leaves"
                    onClick={closeSidebarOnMobile}
                    className={buildNavLinkClassName}
                  >
                    {t("navigation:leavesOverview")}
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/hr/absence-calendar"
                    onClick={closeSidebarOnMobile}
                    className={buildNavLinkClassName}
                  >
                    {t("navigation:absenceCalendar")}
                  </NavLink>
                </li>

                {capabilities.canSeeApprovals && (
                  <li>
                    <NavLink
                      to="/hr/approvals"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:approvals")}
                    </NavLink>
                  </li>
                )}

                <li>
                  <NavLink
                    to="/hr/calendar-exceptions"
                    onClick={closeSidebarOnMobile}
                    className={buildNavLinkClassName}
                  >
                    {t("navigation:calendarExceptions")}
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/hr/work-patterns"
                    onClick={closeSidebarOnMobile}
                    className={buildNavLinkClassName}
                  >
                    {t("navigation:workPatterns")}
                  </NavLink>
                </li>

                {capabilities.canManageWorkSchedules && (
                  <li>
                    <NavLink
                      to="/hr/work-schedules"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:workSchedules")}
                    </NavLink>
                  </li>
                )}
              </ul>
            )}
          </li>

          <li className="sidebar-dropdown">
            <button
              type="button"
              className={`sidebar-dropdown-btn ${isVouchersOpen ? "open" : ""}`}
              onClick={() => toggleDropdown(setIsVouchersOpen)}
              aria-expanded={isVouchersOpen && !isCollapsed}
              aria-controls="submenu-vouchers"
              title={isCollapsed ? t("navigation:vouchers") : undefined}
            >
              <TopDropdownContent icon="vouchers" label={t("navigation:vouchers")} />
            </button>

            {!isCollapsed && isVouchersOpen && (
              <ul id="submenu-vouchers" className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/vouchers/basic-data"
                    onClick={closeSidebarOnMobile}
                    className={buildNavLinkClassName}
                  >
                    {t("navigation:basicData")}
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/vouchers/voucher"
                    onClick={closeSidebarOnMobile}
                    className={buildNavLinkClassName}
                  >
                    {t("navigation:voucher")}
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/vouchers/overview"
                    onClick={closeSidebarOnMobile}
                    className={buildNavLinkClassName}
                  >
                    {t("navigation:voucherOverview")}
                  </NavLink>
                </li>
              </ul>
            )}
          </li>

          {capabilities.canSeeCatalog && (
            <li className="sidebar-dropdown">
              <button
                type="button"
                className={`sidebar-dropdown-btn ${isCatalogOpen ? "open" : ""}`}
                onClick={() => toggleDropdown(setIsCatalogOpen)}
                aria-expanded={isCatalogOpen && !isCollapsed}
                aria-controls="submenu-catalog"
                title={isCollapsed ? t("navigation:catalog") : undefined}
              >
                <TopDropdownContent icon="catalog" label={t("navigation:catalog")} />
              </button>

              {!isCollapsed && isCatalogOpen && (
                <ul id="submenu-catalog" className="sidebar-submenu">
                  <li>
                    <NavLink
                      to="/catalog/service-categories"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:serviceCategories")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/catalog/service-sections"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:serviceSections")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/catalog/services"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:services")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/catalog/product-categories"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:productCategories")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/catalog/products"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:products")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/catalog/publication"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:websitePublication")}
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}

          {/*
            Sales is a top-level reception workflow, but it now behaves like the
            other operational modules in the sidebar: the top-level entry opens a
            dropdown and concrete screens live below it. This avoids the previous
            UX where clicking Sales immediately opened the document list without
            showing the available Sales sub-navigation.

            Current screens:
            - Sales Overview: browse/filter/post/cancel sales documents.
            - New Sales Document: quick access to the editor/create flow.

            Product lines posted from Sales will automatically create stock SALE
            movements on the backend. Sidebar visibility is UX only; backend
            authorization remains authoritative.
          */}
          {capabilities.canSeeSales && (
            <li className="sidebar-dropdown">
              <button
                type="button"
                className={`sidebar-dropdown-btn ${isSalesOpen ? "open" : ""}`}
                onClick={() => toggleDropdown(setIsSalesOpen)}
                aria-expanded={isSalesOpen && !isCollapsed}
                aria-controls="submenu-sales"
                title={isCollapsed ? t("navigation:sales") : undefined}
              >
                <TopDropdownContent icon="sales" label={t("navigation:sales")} />
              </button>

              {!isCollapsed && isSalesOpen && (
                <ul id="submenu-sales" className="sidebar-submenu">
                  <li>
                    <NavLink
                      to="/sales"
                      onClick={closeSidebarOnMobile}
                      end
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:salesOverview")}
                    </NavLink>
                  </li>


                  <li>
                    <NavLink
                      to="/sales/new"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:newSalesDocument")}
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}


          {/*
            Web Shop is separated from Sales because these records originate
            from the public online checkout/payment flow. Internal users can
            inspect orders here, while creation remains owned by the public shop
            and payment webhook. Superuser-only maintenance actions are guarded
            by the backend, not by sidebar visibility.
          */}
          {capabilities.canSeeWebShop && (
            <li className="sidebar-dropdown">
              <button
                type="button"
                className={`sidebar-dropdown-btn ${isWebShopOpen ? "open" : ""}`}
                onClick={() => toggleDropdown(setIsWebShopOpen)}
                aria-expanded={isWebShopOpen && !isCollapsed}
                aria-controls="submenu-web-shop"
                title={isCollapsed ? t("navigation:webShop") : undefined}
              >
                <TopDropdownContent icon="webShop" label={t("navigation:webShop")} />
              </button>

              {!isCollapsed && isWebShopOpen && (
                <ul id="submenu-web-shop" className="sidebar-submenu">
                  <li>
                    <NavLink
                      to="/web-shop/orders"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:webShopOrdersOverview")}
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}

          {/*
            Stock is one operational module, not two independent sidebar areas.
            The previous implementation exposed "Stock" and "Material Review" as
            separate top-level items. That was misleading because Material Review
            is only the exception/review queue created by stock-related service
            consumption processing.

            Keep one top-level Stock entry and show the concrete stock screens as
            submenu items:
            - Stock Overview: manual stock operations, balances, movements.
            - Material Review: non-blocking service-consumption exceptions.
          */}
          {capabilities.canSeeStock && (
            <li className="sidebar-dropdown">
              <button
                type="button"
                className={`sidebar-dropdown-btn ${isStockOpen ? "open" : ""}`}
                onClick={() => toggleDropdown(setIsStockOpen)}
                aria-expanded={isStockOpen && !isCollapsed}
                aria-controls="submenu-stock"
                title={isCollapsed ? t("navigation:stock") : undefined}
              >
                <TopDropdownContent icon="stock" label={t("navigation:stock")} />
              </button>

              {!isCollapsed && isStockOpen && (
                <ul id="submenu-stock" className="sidebar-submenu">
                  <li>
                    <NavLink
                      to="/stock"
                      onClick={closeSidebarOnMobile}
                      end
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:stockOverview")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/stock/locations"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:stockLocations")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/stock/units-of-measure"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:unitsOfMeasure")}
                    </NavLink>
                  </li>

                  {capabilities.canManageStockSettings && (
                    <li>
                      <NavLink
                        to="/stock/unit-conversions"
                        onClick={closeSidebarOnMobile}
                        className={buildNavLinkClassName}
                      >
                        {t("navigation:unitConversions")}
                      </NavLink>
                    </li>
                  )}

                  <li>
                    <NavLink
                      to="/stock/material-exceptions"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:materialReview")}
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}

          {capabilities.canSeeSanitary && (
            <li className="sidebar-dropdown">
              <button
                type="button"
                className={`sidebar-dropdown-btn ${isSanitaryOpen ? "open" : ""}`}
                onClick={() => toggleDropdown(setIsSanitaryOpen)}
                aria-expanded={isSanitaryOpen && !isCollapsed}
                aria-controls="submenu-sanitary"
                title={isCollapsed ? t("navigation:sanitaryDocumentation") : undefined}
              >
                <TopDropdownContent icon="sanitary" label={t("navigation:sanitaryDocumentation")} />
              </button>

              {!isCollapsed && isSanitaryOpen && (
                <ul id="submenu-sanitary" className="sidebar-submenu">
                  <li>
                    <NavLink
                      to="/sanitary/products"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:sanitaryProducts")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/sanitary/rules"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:sanitaryRules")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/sanitary/daily-settlement"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:sanitaryDailySettlement")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/sanitary/events"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:sanitaryEvents")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/sanitary/solutions"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:sanitarySolutions")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/sanitary/work-patterns"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:sanitaryWorkPatterns")}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/sanitary/reports"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:sanitaryReports")}
                    </NavLink>
                  </li>
                  {capabilities.isSuperuser && (
                    <li>
                      <NavLink
                        to="/sanitary/daily-closing"
                        onClick={closeSidebarOnMobile}
                        className={buildNavLinkClassName}
                      >
                        {t("navigation:sanitaryDailyClosing")}
                      </NavLink>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {capabilities.canSeeReports && (
            <li className="sidebar-dropdown">
              <button
                type="button"
                className={`sidebar-dropdown-btn ${isReportsOpen ? "open" : ""}`}
                onClick={() => toggleDropdown(setIsReportsOpen)}
                aria-expanded={isReportsOpen && !isCollapsed}
                aria-controls="submenu-reports"
                title={isCollapsed ? t("navigation:reports") : undefined}
              >
                <TopDropdownContent icon="reports" label={t("navigation:reports")} />
              </button>

              {!isCollapsed && isReportsOpen && (
                <ul id="submenu-reports" className="sidebar-submenu">
                  <li>
                    <NavLink
                      to="/reports"
                      onClick={closeSidebarOnMobile}
                      end
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:reportsHome")}
                    </NavLink>
                  </li>

                  <li className="sidebar-subdropdown">
                    <button
                      type="button"
                      className={`sidebar-subdropdown-btn ${isFinancialReportsOpen ? "open" : ""}`}
                      onClick={() => setIsFinancialReportsOpen((prev) => !prev)}
                      aria-expanded={isFinancialReportsOpen}
                      aria-controls="submenu-reports-financial"
                    >
                      <span>{t("navigation:financialReports")}</span>
                      <span className="caret">▼</span>
                    </button>

                    {isFinancialReportsOpen && (
                      <ul
                        id="submenu-reports-financial"
                        className="sidebar-submenu sidebar-submenu--nested"
                      >
                        {capabilities.canSeeIncomeReport && (
                          <li>
                            <NavLink
                              to="/reports/income"
                              onClick={closeSidebarOnMobile}
                              className={buildNavLinkClassName}
                            >
                              {t("navigation:incomeReport")}
                            </NavLink>
                          </li>
                        )}

                        <li>
                          <NavLink
                            to="/reports/commission"
                            onClick={closeSidebarOnMobile}
                            className={buildNavLinkClassName}
                          >
                            {t("navigation:commissionReport")}
                          </NavLink>
                        </li>
                      </ul>
                    )}
                  </li>

                  {capabilities.canSeeHrReports && (
                    <li className="sidebar-subdropdown">
                      <button
                        type="button"
                        className={`sidebar-subdropdown-btn ${isHrReportsOpen ? "open" : ""}`}
                        onClick={() => setIsHrReportsOpen((prev) => !prev)}
                        aria-expanded={isHrReportsOpen}
                        aria-controls="submenu-reports-hr"
                      >
                        <span>{t("navigation:hrReports")}</span>
                        <span className="caret">▼</span>
                      </button>

                      {isHrReportsOpen && (
                        <ul
                          id="submenu-reports-hr"
                          className="sidebar-submenu sidebar-submenu--nested"
                        >
                          <li>
                            <NavLink
                              to="/reports/hr/absence-payroll"
                              onClick={closeSidebarOnMobile}
                              className={buildNavLinkClassName}
                            >
                              {t("navigation:absencePayrollReport")}
                            </NavLink>
                          </li>
                        </ul>
                      )}
                    </li>
                  )}

                  <li className="sidebar-subdropdown">
                    <button
                      type="button"
                      className={`sidebar-subdropdown-btn ${isVisitsAnalyticsOpen ? "open" : ""}`}
                      onClick={() => setIsVisitsAnalyticsOpen((prev) => !prev)}
                      aria-expanded={isVisitsAnalyticsOpen}
                      aria-controls="submenu-reports-visits-analytics"
                    >
                      <span>{t("navigation:visitAnalytics")}</span>
                      <span className="caret">▼</span>
                    </button>

                    {isVisitsAnalyticsOpen && (
                      <ul
                        id="submenu-reports-visits-analytics"
                        className="sidebar-submenu sidebar-submenu--nested"
                      >
                        <li className="sidebar-subdropdown sidebar-subdropdown--level-3">
                          <button
                            type="button"
                            className={`sidebar-subdropdown-btn sidebar-subdropdown-btn--level-3 ${isVisitsCapacityOpen ? "open" : ""}`}
                            onClick={() => setIsVisitsCapacityOpen((prev) => !prev)}
                            aria-expanded={isVisitsCapacityOpen}
                            aria-controls="submenu-reports-visits-capacity"
                          >
                            <span>{t("navigation:capacityUtilization")}</span>
                            <span className="caret">▼</span>
                          </button>

                          {isVisitsCapacityOpen && (
                            <ul
                              id="submenu-reports-visits-capacity"
                              className="sidebar-submenu sidebar-submenu--nested sidebar-submenu--nested-2"
                            >
                              <li>
                                <NavLink
                                  to="/reports/slot-performance"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:slotPerformance")}
                                </NavLink>
                              </li>

                              <li>
                                <NavLink
                                  to="/reports/idle-windows"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:idleWindows")}
                                </NavLink>
                              </li>

                              <li>
                                <NavLink
                                  to="/reports/daily-schedule-quality"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:dailyScheduleQuality")}
                                </NavLink>
                              </li>
                            </ul>
                          )}
                        </li>

                        <li className="sidebar-subdropdown sidebar-subdropdown--level-3">
                          <button
                            type="button"
                            className={`sidebar-subdropdown-btn sidebar-subdropdown-btn--level-3 ${isVisitsEconomicsOpen ? "open" : ""}`}
                            onClick={() => setIsVisitsEconomicsOpen((prev) => !prev)}
                            aria-expanded={isVisitsEconomicsOpen}
                            aria-controls="submenu-reports-visits-economics"
                          >
                            <span>{t("navigation:revenueEconomics")}</span>
                            <span className="caret">▼</span>
                          </button>

                          {isVisitsEconomicsOpen && (
                            <ul
                              id="submenu-reports-visits-economics"
                              className="sidebar-submenu sidebar-submenu--nested sidebar-submenu--nested-2"
                            >
                              <li>
                                <NavLink
                                  to="/reports/service-profitability"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:serviceProfitability")}
                                </NavLink>
                              </li>

                              <li>
                                <NavLink
                                  to="/reports/category-economics"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:categoryEconomics")}
                                </NavLink>
                              </li>

                              <li>
                                <NavLink
                                  to="/reports/weekday-economics"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:weekdayEconomics")}
                                </NavLink>
                              </li>

                              <li>
                                <NavLink
                                  to="/reports/premium-windows"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:premiumWindow")}
                                </NavLink>
                              </li>
                            </ul>
                          )}
                        </li>

                        <li className="sidebar-subdropdown sidebar-subdropdown--level-3">
                          <button
                            type="button"
                            className={`sidebar-subdropdown-btn sidebar-subdropdown-btn--level-3 ${isVisitsClientOpen ? "open" : ""}`}
                            onClick={() => setIsVisitsClientOpen((prev) => !prev)}
                            aria-expanded={isVisitsClientOpen}
                            aria-controls="submenu-reports-visits-client"
                          >
                            <span>{t("navigation:clientRetention")}</span>
                            <span className="caret">▼</span>
                          </button>

                          {isVisitsClientOpen && (
                            <ul
                              id="submenu-reports-visits-client"
                              className="sidebar-submenu sidebar-submenu--nested sidebar-submenu--nested-2"
                            >
                              <li>
                                <NavLink
                                  to="/reports/no-show-loss"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:noShowLoss")}
                                </NavLink>
                              </li>

                              <li>
                                <NavLink
                                  to="/reports/client-retention-lite"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:clientRetentionLite")}
                                </NavLink>
                              </li>
                            </ul>
                          )}
                        </li>

                        <li className="sidebar-subdropdown sidebar-subdropdown--level-3">
                          <button
                            type="button"
                            className={`sidebar-subdropdown-btn sidebar-subdropdown-btn--level-3 ${isVisitsTrendOpen ? "open" : ""}`}
                            onClick={() => setIsVisitsTrendOpen((prev) => !prev)}
                            aria-expanded={isVisitsTrendOpen}
                            aria-controls="submenu-reports-visits-trend"
                          >
                            <span>{t("navigation:trendsPatterns")}</span>
                            <span className="caret">▼</span>
                          </button>

                          {isVisitsTrendOpen && (
                            <ul
                              id="submenu-reports-visits-trend"
                              className="sidebar-submenu sidebar-submenu--nested sidebar-submenu--nested-2"
                            >
                              <li>
                                <NavLink
                                  to="/reports/trend-seasonality"
                                  onClick={closeSidebarOnMobile}
                                  className={buildNavLinkClassName}
                                >
                                  {t("navigation:trendSeasonality")}
                                </NavLink>
                              </li>
                            </ul>
                          )}
                        </li>
                      </ul>
                    )}
                  </li>
                </ul>
              )}
            </li>
          )}

          {capabilities.canSeeAdministration && (
            <li className="sidebar-dropdown">
              <button
                type="button"
                className={`sidebar-dropdown-btn ${isAdminOpen ? "open" : ""}`}
                onClick={() => toggleDropdown(setIsAdminOpen)}
                aria-expanded={isAdminOpen && !isCollapsed}
                aria-controls="submenu-admin"
                title={isCollapsed ? t("navigation:administration") : undefined}
              >
                <TopDropdownContent icon="admin" label={t("navigation:administration")} />
              </button>

              {!isCollapsed && isAdminOpen && (
                <ul id="submenu-admin" className="sidebar-submenu">
                  <li>
                    <NavLink
                      to="/admin-panel"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:adminPanel")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/admin/users"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:users")}
                    </NavLink>
                  </li>

                  <li>
                    <NavLink
                      to="/admin/master-data/leave-types"
                      onClick={closeSidebarOnMobile}
                      className={buildNavLinkClassName}
                    >
                      {t("navigation:leaveTypes")}
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}
        </ul>
      </div>

      <div className="sidebar-footer">
        {!isCollapsed && <LanguageSwitcher compact={false} />}

        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={toggleCollapsed}
          aria-label={isCollapsed ? t("common:actions.expand") : t("common:actions.collapseMenu")}
          title={isCollapsed ? t("common:actions.expand") : t("common:actions.collapseMenu")}
        >
          <span className="sidebar-collapse-icon" aria-hidden="true">
            {isCollapsed ? "›" : "‹"}
          </span>
          <span className="sidebar-collapse-label">
            {isCollapsed ? t("common:actions.expand") : t("common:actions.collapseMenu")}
          </span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;








